import {
  cidrToRange,
  decomposeRangeToCidrs,
  formatCidr,
  parseCidr,
  prefixToMask,
} from './cidrUtils';
import type { AddressRange, CidrBlock } from './types';

export type CanvasGapSegment = {
  kind: 'gap';
  id: string;
  range: AddressRange;
};

export type CanvasBlockSegment = {
  kind: 'block';
  id: string;
  block: CidrBlock;
  prefix: number;
  range: AddressRange;
};

export type CanvasSegment = CanvasGapSegment | CanvasBlockSegment;

export type LayoutSegment = CanvasSegment & {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type CanvasLayoutResult = {
  segments: LayoutSegment[];
  viewBoxWidth: number;
  viewBoxHeight: number;
  grid: GridSpec;
  cellSize: number;
};

export type GridSpec = {
  cols: number;
  rows: number;
  cellCount: number;
  cellPrefix: number;
  splitDepth: number;
};

const VIEWBOX_SIZE = 1000;
const CANVAS_PADDING = 10;
const CELL_GAP = 1;
const CANVAS_HEIGHT = VIEWBOX_SIZE;
const MAX_SPLIT_DEPTH = 8;

/**
 * 親 CIDR が取りうる最大分割深度（キャンバスサイズはここで固定）
 */
export function computeMaxSplitDepth(parentPrefix: number): number {
  return Math.min(MAX_SPLIT_DEPTH, 32 - parentPrefix);
}

/**
 * 現在のブロック深度（表示中の最細プレフィックス）
 */
export function computeGridSplitDepth(parentPrefix: number, blocks: CidrBlock[]): number {
  if (blocks.length === 0) {
    return 0;
  }

  const maxBlockPrefix = Math.max(...blocks.map((block) => parseCidr(block.cidr).prefix));
  const depth = Math.max(0, maxBlockPrefix - parentPrefix);
  return Math.min(depth, MAX_SPLIT_DEPTH, 32 - parentPrefix);
}

/**
 * 親 CIDR から固定グリッドを決める（分割してもキャンバス外寸は変わらない）
 */
export function computeGridSpec(parentCidr: string, blocks: CidrBlock[] = []): GridSpec {
  const parentPrefix = parseCidr(parentCidr).prefix;
  const maxSplitDepth = computeMaxSplitDepth(parentPrefix);
  const splitDepth = computeGridSplitDepth(parentPrefix, blocks);
  const rows = maxSplitDepth === 0 ? 1 : 2 ** Math.ceil(maxSplitDepth / 2);
  const cols = maxSplitDepth === 0 ? 1 : 2 ** Math.floor(maxSplitDepth / 2);
  const cellCount = rows * cols;
  const cellPrefix = parentPrefix + maxSplitDepth;

  return { cols, rows, cellCount, cellPrefix, splitDepth };
}

/**
 * 親 CIDR 内のブロックと空きを IP 順セグメントに変換する
 */
export function buildCanvasSegments(parentCidr: string, blocks: CidrBlock[]): CanvasSegment[] {
  const parent = cidrToRange(parentCidr);
  const sorted = [...blocks].sort((a, b) => {
    const aStart = cidrToRange(a.cidr).start;
    const bStart = cidrToRange(b.cidr).start;
    if (aStart < bStart) return -1;
    if (aStart > bStart) return 1;
    return parseCidr(a.cidr).prefix - parseCidr(b.cidr).prefix;
  });

  const segments: CanvasSegment[] = [];
  let cursor = parent.start;

  for (const block of sorted) {
    const range = cidrToRange(block.cidr);
    if (range.end < parent.start || range.start > parent.end) {
      continue;
    }

    const clampedStart = range.start < parent.start ? parent.start : range.start;
    const clampedEnd = range.end > parent.end ? parent.end : range.end;

    if (clampedStart > cursor) {
      segments.push({
        kind: 'gap',
        id: `gap-${cursor.toString()}`,
        range: { start: cursor, end: clampedStart - BigInt(1) },
      });
    }

    segments.push({
      kind: 'block',
      id: block.id,
      block,
      prefix: parseCidr(block.cidr).prefix,
      range: { start: clampedStart, end: clampedEnd },
    });

    cursor = clampedEnd + BigInt(1);
  }

  if (cursor <= parent.end) {
    segments.push({
      kind: 'gap',
      id: `gap-${cursor.toString()}`,
      range: { start: cursor, end: parent.end },
    });
  }

  return segments;
}

type GridBounds = {
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
};

/**
 * CIDR の2進分割に沿ってグリッド上の矩形を求める
 */
export function cidrToGridBounds(
  parentCidr: string,
  blockCidr: string,
  cols: number,
  rows: number,
): GridBounds {
  const parent = parseCidr(parentCidr);
  const block = parseCidr(blockCidr);

  let col = 0;
  let row = 0;
  let colSpan = cols;
  let rowSpan = rows;

  for (let prefix = parent.prefix + 1; prefix <= block.prefix; prefix += 1) {
    const bitIndex = 32 - prefix;
    const bit = Number((block.network >> BigInt(bitIndex)) & BigInt(1));
    const depth = prefix - parent.prefix;
    const splitRows = depth % 2 === 1;

    if (splitRows) {
      const half = rowSpan / 2;
      if (bit === 1) {
        row += half;
      }
      rowSpan = half;
    } else {
      const half = colSpan / 2;
      if (bit === 1) {
        col += half;
      }
      colSpan = half;
    }
  }

  return { col, row, colSpan, rowSpan };
}

function boundsToLayout(
  segment: CanvasSegment,
  bounds: GridBounds,
  cellSize: number,
): LayoutSegment {
  const x = CANVAS_PADDING + bounds.col * cellSize;
  const y = CANVAS_PADDING + bounds.row * cellSize;
  const width = bounds.colSpan * cellSize - CELL_GAP;
  const height = bounds.rowSpan * cellSize - CELL_GAP;

  return {
    ...segment,
    x,
    y,
    width: Math.max(width, 1),
    height: Math.max(height, 1),
  };
}

function layoutFineBlock(
  segment: CanvasBlockSegment,
  parentCidr: string,
  grid: GridSpec,
  cellSize: number,
): LayoutSegment {
  const cellPrefix = grid.cellPrefix;
  const mask = prefixToMask(cellPrefix);
  const cellNetwork = segment.range.start & mask;
  const cellCidr = formatCidr(cellNetwork, cellPrefix);
  const cellBounds = cidrToGridBounds(parentCidr, cellCidr, grid.cols, grid.rows);

  const subDepth = segment.prefix - cellPrefix;
  const subCols = subDepth === 0 ? 1 : 2 ** Math.floor(subDepth / 2);
  const subRows = subDepth === 0 ? 1 : 2 ** Math.ceil(subDepth / 2);
  const subBounds = cidrToGridBounds(cellCidr, segment.block.cidr, subCols, subRows);

  const cellPixelW = cellBounds.colSpan * cellSize;
  const cellPixelH = cellBounds.rowSpan * cellSize;

  return {
    ...segment,
    x: CANVAS_PADDING + cellBounds.col * cellSize + (subBounds.col / subCols) * cellPixelW,
    y: CANVAS_PADDING + cellBounds.row * cellSize + (subBounds.row / subRows) * cellPixelH,
    width: Math.max((subBounds.colSpan / subCols) * cellPixelW - CELL_GAP, 1),
    height: Math.max((subBounds.rowSpan / subRows) * cellPixelH - CELL_GAP, 1),
  };
}

function layoutBlockSegment(
  segment: CanvasBlockSegment,
  parentCidr: string,
  grid: GridSpec,
  cellSize: number,
): LayoutSegment {
  if (segment.prefix > grid.cellPrefix) {
    return layoutFineBlock(segment, parentCidr, grid, cellSize);
  }

  const bounds = cidrToGridBounds(parentCidr, segment.block.cidr, grid.cols, grid.rows);
  return boundsToLayout(segment, bounds, cellSize);
}

function layoutGapSegment(
  segment: CanvasGapSegment,
  parentCidr: string,
  grid: GridSpec,
  cellSize: number,
): LayoutSegment[] {
  return decomposeRangeToCidrs(segment.range).map((cidr, index) => {
    const prefix = parseCidr(cidr).prefix;
    const blockSegment: CanvasBlockSegment = {
      kind: 'block',
      id: `${segment.id}-${index}`,
      block: { id: `${segment.id}-${index}`, cidr, name: '' },
      prefix,
      range: cidrToRange(cidr),
    };
    const layout = layoutBlockSegment(blockSegment, parentCidr, grid, cellSize);

    return {
      kind: 'gap' as const,
      id: `${segment.id}-${index}`,
      range: cidrToRange(cidr),
      x: layout.x,
      y: layout.y,
      width: layout.width,
      height: layout.height,
    };
  });
}

function compareDrawOrder(a: LayoutSegment, b: LayoutSegment): number {
  if (a.kind !== b.kind) {
    return a.kind === 'gap' ? -1 : 1;
  }

  if (a.kind === 'block' && b.kind === 'block') {
    return a.prefix - b.prefix;
  }

  return 0;
}

/**
 * セグメントを CIDR 2 進分割グリッド上に配置する
 */
export function layoutCanvasSegments(
  segments: CanvasSegment[],
  parentCidr: string,
  blocks: CidrBlock[] = [],
): CanvasLayoutResult {
  const grid = computeGridSpec(parentCidr, blocks);
  const maxDim = Math.max(grid.cols, grid.rows);
  const cellSize = (VIEWBOX_SIZE - CANVAS_PADDING * 2) / maxDim;
  const viewBoxWidth = VIEWBOX_SIZE;
  const viewBoxHeight = VIEWBOX_SIZE;

  const laidOut: LayoutSegment[] = [];

  for (const segment of segments) {
    if (segment.kind === 'block') {
      laidOut.push(layoutBlockSegment(segment, parentCidr, grid, cellSize));
      continue;
    }

    laidOut.push(...layoutGapSegment(segment, parentCidr, grid, cellSize));
  }

  laidOut.sort(compareDrawOrder);

  return {
    segments: laidOut,
    viewBoxWidth,
    viewBoxHeight,
    grid,
    cellSize,
  };
}

export { VIEWBOX_SIZE as VIEWBOX_WIDTH, CANVAS_HEIGHT };
